import { inject, injectable } from 'tsyringe';
import { ConditionsSummary } from './models/conditions-summary.model';
import { TrackEvent } from 'app-core/src/models/tracking-event.model';
import { ContentRecommendationUserAction } from 'app-core/src/constants/content-recommendation-user-action.enum';
import { ABTestParams } from './models/ab-test-params.model';
import { TrackingExtra } from './models/tracking-extra.model';
import { Tracking } from './models/tracking.model';
import {
  kDIOWOWSettingsGamesWrapper,
  kDITOWExtensionWrapper,
  kDITOWTrackRSWrapper,
  kDITOWUtilsWrapper,
} from 'app-core/src/constants/injectionTokens';
import { IOwTrackRSEventWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-track-rs-event.interface';
import { IOWUtilsWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-utils-wrapper.interface';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import { RecommendationDatastoreService } from '../datastore-service/recommendation-datastore.service';
import { LoggerService } from '../logger.service';
import { IOWSettingsGamesWrapper } from '../../interfaces/ow-wrappers/ow-settings-games-wrapper.interface';

// -----------------------------------------------------------------------------
@injectable()
export class NotificationAnalyticsTracker {
  // ---------------------------------------------------------------------------
  private readonly logger = LoggerService.getCategory(
    'NotificationAnalyticsTracker',
  );

  private phasePercent: number;
  private abTestParams: ABTestParams;
  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITOWTrackRSWrapper)
    private readonly OWtracking: IOwTrackRSEventWrapper,
    @inject(kDITOWExtensionWrapper)
    private readonly owExtensionWrapper: IOWExtensionWrapper,
    @inject(kDITOWUtilsWrapper)
    private readonly owUtilsWrapper: IOWUtilsWrapper,
    private readonly datastoreService: RecommendationDatastoreService,
    @inject(kDIOWOWSettingsGamesWrapper)
    private readonly oWSettingsGamesWrapper: IOWSettingsGamesWrapper,
  ) {
    this.owExtensionWrapper.initialize();
    this.phasePercent = 0;
  }

  // ------------------------------------------------------------------------
  async sendAnalyticsError(gameId: number, applicationUid: string) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'content_recommend_error';
    const extraData: TrackingExtra = {};

    const overrides = {
      InGameId: gameId,
      Value: 1,
    };

    extraData.appVersion = this.getAppVersion();
    extraData.app_id = applicationUid;

    this.sendAnalyticsEvent({ name, extraData, overrides });
  }

  // ------------------------------------------------------------------------
  async sendPortfolioExhausted(gameId: number) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'content_recommend_missed_opportunity_exhausted';
    const extraData = {
      game_id: gameId,
      appVersion: this.getAppVersion(),
    };

    const overrides = {};
    this.sendAnalyticsEvent({ name, extraData, overrides });
  }

  // ---------------------------------------------------------------------------
  async sendConditionsSummary(conditionsSummary: ConditionsSummary) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'content_recommend_missed_opportunity_summary';
    const conditionsSummaryJson = JSON.stringify(conditionsSummary);
    const extraData = {
      conditionsSummaryJson,
      appVersion: this.getAppVersion(),
    };

    const overrides = {};
    this.sendAnalyticsEvent({ name, extraData, overrides });
  }

  // ---------------------------------------------------------------------------
  async sendContentRecommend(
    gameId: number,
    applicationUid: string,
    userAction: ContentRecommendationUserAction,
    visibilityTimeInSeconds?: number,
  ) {
    if (typeof gameId === 'string') {
      gameId = parseInt(gameId);
    }

    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const configs = await this.datastoreService.getGlobalConfigurations();

    const cooldownFromPreviousNotificationInHours =
      configs?.cooldownFromPreviousNotificationInHours;
    const installApps = await this.getInstalledApps();

    const name = 'content_recommend';
    const gameOverlay = (await this.oWSettingsGamesWrapper.getOverlayEnabled(
      gameId,
    ))
      ? 'enabled'
      : 'disabled';

    const extraData: TrackingExtra = {
      crn_app: applicationUid,
      appVersion: this.getAppVersion(),
      cooldown: cooldownFromPreviousNotificationInHours,
      game_overlay_state: gameOverlay,
    };

    if (visibilityTimeInSeconds) {
      extraData.dismiss_counter = visibilityTimeInSeconds;
    }

    if (installApps) {
      extraData.installed_apps = installApps.toString();
    }

    const overrides = {
      InGameId: gameId,
      Value: userAction,
    };
    this.sendAnalyticsEvent({ name, extraData, overrides });
  }

  // ------------------------------------------------------------------------
  async sendContentViewEvent(event: TrackEvent) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = event.name;
    const extraData = event.extra;
    extraData.appVersion = this.getAppVersion();
    const overrides = event.overrides;
    if (!name || !extraData) {
      return;
    }

    this.sendAnalyticsEvent({ name, extraData, overrides });
  }

  // ---------------------------------------------------------------------------
  ABTestStarted(abTestParams: ABTestParams) {
    this.abTestParams = abTestParams;
  }

  // ---------------------------------------------------------------------------
  private getAppVersion() {
    const { manifest } = this.owExtensionWrapper;
    return manifest.meta.version;
  }

  // ---------------------------------------------------------------------------
  private async getInstalledApps() {
    return this.owExtensionWrapper.getInstalledAndEnabledExtensionUids();
  }

  // ---------------------------------------------------------------------------
  private sendAnalyticsEvent(data: Tracking) {
    this.appendABTestDataIfNeeded(data);
    this.OWtracking.trackRSEvent(data.name, data.extraData, data.overrides);
  }

  // ---------------------------------------------------------------------------
  private appendABTestDataIfNeeded(data: any) {
    if (this.abTestParams) {
      data.extraData.experience_name = this.abTestParams.experience_name;
      data.extraData.version_viewed = this.abTestParams.version_viewed;
    }
  }

  // ---------------------------------------------------------------------------
  private async shouldTrack() {
    return this.isPhasingInRange();
  }

  // ---------------------------------------------------------------------------
  private async isPhasingInRange() {
    if (this.phasePercent === 0) {
      this.phasePercent =
        await this.owUtilsWrapper.getPersistentPhasedPercent();
    }

    const configs = await this.datastoreService.getGlobalConfigurations();

    const configuredAnalyticsPhasing =
      configs?.passiveAnalyticsPersistentPhasing;

    const result = this.phasePercent <= configuredAnalyticsPhasing;
    return result;
  }
}
